'use client';
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { clsx } from 'clsx';
import { Icon } from 'components/UI/Icon';
import { UUID } from 'utils/idGenerator';
import styles from './Swiper.module.scss';

type SwiperProps = {
  readonly title?: string;
  readonly items: ReadonlyArray<ReactElement>;
  readonly identifier?: string;
  readonly variant?: 'news' | 'links';
  readonly anchor?: string;
};

const Swiper = ({ title, items, variant = 'links', anchor }: SwiperProps) => {
  const [showArrows, setShowArrows] = useState(false);
  const [scrolledToStart, setScrolledToStart] = useState(true);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);

  const sliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sliderRefCurrent = sliderRef.current;
    if (!sliderRefCurrent) return;

    const update = () => {
      const { scrollLeft, scrollWidth, clientWidth } = sliderRefCurrent;
      setShowArrows(scrollWidth > clientWidth);
      setScrolledToStart(scrollLeft <= 0);
      setScrolledToEnd(scrollLeft + clientWidth >= scrollWidth - 1);
    };

    const resizeObserver = new ResizeObserver(() => {
      update();
    });
    resizeObserver.observe(sliderRefCurrent);

    sliderRefCurrent.addEventListener('scroll', update, { passive: true });

    return () => {
      resizeObserver.disconnect();
      sliderRefCurrent.removeEventListener('scroll', update);
    };
  }, []);

  const slide = (direction: 'prev' | 'next') => {
    const sliderRefCurrent = sliderRef.current;
    if (!sliderRefCurrent) return;

    const { scrollLeft, clientWidth } = sliderRefCurrent;
    const left = direction === 'prev' ? scrollLeft - clientWidth : scrollLeft + clientWidth;
    sliderRefCurrent.scroll({ left, behavior: 'smooth' });
  };

  return (
    <section className={styles.section} id={anchor}>
      <div className={styles.header}>
        {title &&
          (variant === 'news' ? (
            <div className={clsx(styles.title, styles.news, 'swiperTitle')}>{title}</div>
          ) : (
            <h3 className={clsx(styles.title, styles.links, 'swiperTitle')}>{title}</h3>
          ))}
      </div>

      {showArrows && (
        <>
          <button
            className={clsx(styles.arrow, styles.prev)}
            onClick={() => slide('prev')}
            aria-label="Zurück"
            disabled={scrolledToStart}
          >
            <Icon name="chevronLeft" />
          </button>
          <button
            className={clsx(styles.arrow, styles.next)}
            onClick={() => slide('next')}
            aria-label="Weiter"
            disabled={scrolledToEnd}
          >
            <Icon name="chevronRight" />
          </button>
        </>
      )}

      <div ref={sliderRef} className={styles.slider}>
        {items.map((item) => (
          <div key={UUID()} className={styles.teaserWrapper}>
            <div
              className={clsx(styles.teaser, 'swiperTeaser', {
                [styles.links]: variant === 'links',
                [styles.news]: variant === 'news',
              })}
            >
              {item}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Swiper;
